import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  Building2, TrendingUp, Users, CreditCard, Clock, XCircle, BarChart3,
  Loader2, ArrowUpRight, Shield, ChevronDown, ChevronUp, Activity,
} from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || window.location.origin);

const STATUS_CONFIG = {
  active: { label: "Actif", color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30" },
  trialing: { label: "Essai", color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/30" },
  canceled: { label: "Annule", color: "text-red-400", bg: "bg-red-500/10 border-red-500/30" },
  past_due: { label: "Impaye", color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/30" },
};

const PLAN_COLORS = {
  trial: "text-slate-400",
  starter: "text-cyan-400",
  pro: "text-violet-400",
  agency: "text-amber-400",
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}

function KPICard({ icon: Icon, label, value, sub, color = "from-cyan-500 to-blue-500" }) {
  return (
    <div className="bg-[#0F1629] border border-slate-700/50 rounded-xl p-5 flex items-start gap-4" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-slate-400 text-xs font-medium uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-white mt-0.5">{value}</p>
        {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

export default function SuperAdminPage() {
  const { token } = useAuth();
  const [overview, setOverview] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [editingId, setEditingId] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchData = useCallback(async () => {
    try {
      const [ov, ag] = await Promise.all([
        fetch(`${API}/api/super-admin/overview`, { headers }).then((r) => r.json()),
        fetch(`${API}/api/super-admin/agencies`, { headers }).then((r) => r.json()),
      ]);
      setOverview(ov);
      setAgencies(ag);
    } catch (e) {
      console.error("Super admin fetch error:", e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const handleUpdateAgency = async (agencyId, updates) => {
    await fetch(`${API}/api/super-admin/agencies/${agencyId}`, {
      method: "PUT",
      headers,
      body: JSON.stringify(updates),
    });
    setEditingId(null);
    fetchData();
  };

  const sorted = [...agencies].sort((a, b) => {
    const va = a[sortField] ?? "";
    const vb = b[sortField] ?? "";
    if (sortDir === "asc") return va > vb ? 1 : -1;
    return va < vb ? 1 : -1;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === "asc" ? <ChevronUp className="w-3 h-3 inline ml-1" /> : <ChevronDown className="w-3 h-3 inline ml-1" />;
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="super-admin-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-red-500 flex items-center justify-center">
          <Shield className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Super Admin</h1>
          <p className="text-slate-400 text-sm">Pilotage de la plateforme GLOTECH OS</p>
        </div>
      </div>

      {/* KPI Cards */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <KPICard
            icon={TrendingUp}
            label="MRR"
            value={`${overview.mrr.toLocaleString("fr-FR")} EUR`}
            sub={`ARR: ${overview.arr.toLocaleString("fr-FR")} EUR`}
            color="from-emerald-500 to-teal-500"
          />
          <KPICard
            icon={Building2}
            label="Agences"
            value={overview.total_agencies}
            sub={`${overview.new_this_month} ce mois`}
            color="from-cyan-500 to-blue-500"
          />
          <KPICard
            icon={CreditCard}
            label="Actives"
            value={overview.active_agencies}
            color="from-violet-500 to-purple-500"
          />
          <KPICard
            icon={Clock}
            label="En essai"
            value={overview.trialing_agencies}
            color="from-blue-500 to-indigo-500"
          />
          <KPICard
            icon={XCircle}
            label="Annulees"
            value={overview.canceled_agencies}
            sub={`Churn: ${overview.churn_rate}%`}
            color="from-red-500 to-pink-500"
          />
        </div>
      )}

      {/* Revenue Bar */}
      {overview && overview.mrr > 0 && (
        <div className="bg-[#0F1629] border border-slate-700/50 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-slate-400">Repartition MRR par plan</span>
            <span className="text-sm font-bold text-white">{overview.mrr.toLocaleString("fr-FR")} EUR/mois</span>
          </div>
          <div className="flex rounded-lg overflow-hidden h-4 bg-slate-800">
            {["starter", "pro", "agency"].map((plan) => {
              const count = agencies.filter(
                (a) => a.plan === plan && a.subscription_status === "active"
              ).length;
              const revenue = count * ({"starter": 49, "pro": 149, "agency": 349}[plan] || 0);
              const pct = overview.mrr > 0 ? (revenue / overview.mrr) * 100 : 0;
              if (pct === 0) return null;
              const colors = { starter: "bg-cyan-500", pro: "bg-violet-500", agency: "bg-amber-500" };
              return (
                <div
                  key={plan}
                  className={`${colors[plan]} transition-all`}
                  style={{ width: `${pct}%` }}
                  title={`${plan}: ${count} agences = ${revenue} EUR`}
                />
              );
            })}
          </div>
          <div className="flex gap-6 mt-2">
            {[
              { plan: "Starter", color: "bg-cyan-500", price: 49 },
              { plan: "Pro", color: "bg-violet-500", price: 149 },
              { plan: "Agency", color: "bg-amber-500", price: 349 },
            ].map((p) => (
              <div key={p.plan} className="flex items-center gap-1.5 text-xs text-slate-400">
                <div className={`w-2 h-2 rounded-full ${p.color}`} /> {p.plan} ({p.price} EUR)
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Agencies Table */}
      <div className="bg-[#0F1629] border border-slate-700/50 rounded-xl overflow-hidden">
        <div className="p-5 border-b border-slate-700/50 flex items-center justify-between">
          <h2 className="text-lg font-bold text-white">Agences ({agencies.length})</h2>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Activity className="w-3.5 h-3.5" /> Donnees en temps reel
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="agencies-table">
            <thead>
              <tr className="border-b border-slate-700/50">
                {[
                  { key: "name", label: "Agence" },
                  { key: "plan", label: "Plan" },
                  { key: "subscription_status", label: "Statut" },
                  { key: "clients_count", label: "Clients" },
                  { key: "users_count", label: "Utilisateurs" },
                  { key: "created_at", label: "Inscription" },
                  { key: "trial_days_left", label: "Essai" },
                ].map((col) => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    className="text-left text-slate-400 text-xs font-medium px-5 py-3 uppercase tracking-wide cursor-pointer hover:text-white transition-colors select-none"
                  >
                    {col.label}
                    <SortIcon field={col.key} />
                  </th>
                ))}
                <th className="text-right text-slate-400 text-xs font-medium px-5 py-3 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((agency) => {
                const sc = STATUS_CONFIG[agency.subscription_status] || STATUS_CONFIG.trialing;
                const planColor = PLAN_COLORS[agency.plan] || "text-slate-400";
                return (
                  <tr key={agency.id} className="border-b border-slate-800/50 hover:bg-[#151D30] transition-colors" data-testid={`agency-row-${agency.id}`}>
                    <td className="px-5 py-3.5">
                      <div>
                        <span className="text-white font-medium">{agency.name}</span>
                        {agency.slug === "glotech-digital" && (
                          <span className="ml-2 text-[10px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded font-bold">OWNER</span>
                        )}
                      </div>
                      <span className="text-xs text-slate-500">{agency.email}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`font-semibold capitalize ${planColor}`}>{agency.plan_display || agency.plan}</span>
                      {agency.plan !== "trial" && (
                        <span className="text-xs text-slate-500 ml-1">{agency.plan_price} EUR/m</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full border ${sc.bg} ${sc.color}`}>
                        {sc.label}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-slate-300">{agency.clients_count}</td>
                    <td className="px-5 py-3.5 text-slate-300">{agency.users_count}</td>
                    <td className="px-5 py-3.5 text-slate-400 text-xs">{formatDate(agency.created_at)}</td>
                    <td className="px-5 py-3.5">
                      {agency.trial_active ? (
                        <span className="text-blue-400 text-xs font-medium">{agency.trial_days_left}j restants</span>
                      ) : agency.plan === "trial" ? (
                        <span className="text-red-400 text-xs">Expire</span>
                      ) : (
                        <span className="text-slate-500 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      {editingId === agency.id ? (
                        <div className="flex items-center gap-1 justify-end">
                          {["starter", "pro", "agency"].map((p) => (
                            <button
                              key={p}
                              onClick={() => handleUpdateAgency(agency.id, { plan: p, subscription_status: "active" })}
                              className="text-xs px-2 py-1 rounded bg-slate-700 text-white hover:bg-slate-600 transition-colors capitalize"
                            >
                              {p}
                            </button>
                          ))}
                          <button
                            onClick={() => setEditingId(null)}
                            className="text-xs px-2 py-1 rounded text-slate-400 hover:text-white"
                          >
                            x
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setEditingId(agency.id)}
                          data-testid={`edit-agency-${agency.id}`}
                          className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
                        >
                          Modifier
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-slate-500 py-10">Aucune agence inscrite</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Data Management Section */}
      <div className="bg-[#0F1629] border border-slate-700/50 rounded-xl p-6" data-testid="data-management-section">
        <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-red-400" />
          Gestion des donnees
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {["leads", "clients", "proposals", "invoices", "contracts", "projects", "tickets", "documents"].map(coll => (
            <button key={coll}
              onClick={async () => {
                if (!window.confirm(`Vider la collection "${coll}" ? Cette action est irreversible.`)) return;
                try {
                  const res = await fetch(`${API}/api/super-admin/reset-data`, {
                    method: "POST", headers, body: JSON.stringify({ collection: coll })
                  });
                  const d = await res.json();
                  alert(d.message || "OK");
                  fetchData();
                } catch { alert("Erreur"); }
              }}
              data-testid={`purge-${coll}-btn`}
              className="bg-red-500/5 border border-red-500/20 text-red-400 rounded-lg px-3 py-2.5 text-xs font-medium hover:bg-red-500/10 transition-colors capitalize">
              Vider {coll}
            </button>
          ))}
        </div>
        <button
          onClick={async () => {
            if (!window.confirm("REINITIALISER TOUTE L'APPLICATION ? Seul le compte admin sera conserve.")) return;
            if (!window.confirm("Etes-vous ABSOLUMENT certain ? Toutes les donnees seront perdues.")) return;
            try {
              const res = await fetch(`${API}/api/super-admin/reset-data`, {
                method: "POST", headers, body: JSON.stringify({ purge_all: true })
              });
              const d = await res.json();
              alert(d.message || "OK");
              fetchData();
            } catch { alert("Erreur"); }
          }}
          data-testid="reset-all-btn"
          className="mt-4 w-full bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg px-4 py-3 text-sm font-bold hover:bg-red-500/20 transition-colors">
          Reinitialiser toute l'application
        </button>
      </div>
    </div>
  );
}